import { GitBranch, Search } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import type { ReleaseChannelRow } from '../api';
import { StatusChip } from '../components/Avatar';
import { CopyButton } from '../components/CopyButton';
import { EmptyState } from '../components/EmptyState';
import { SectionCard } from '../components/SectionCard';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../hooks/useActionDialog';
import {
  formatArtifactSummary,
  formatRelativeDate,
  getArtifactTechnicalId,
} from '../utils/formatters';

type ChannelsPageProps = {
  openDialog: UseActionDialogReturn['openDialog'];
};

function ChannelsPage({ openDialog }: ChannelsPageProps) {
  const {
    activeProject,
    activeEnvironment,
    artifactById,
    releaseAppById,
    releaseAppVersionById,
    latestPromotionByLane,
    channels,
  } = useWorkspace();

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'serving' | 'empty' | 'paused'
  >('all');

  const servingCount = channels.filter(
    (channel) => channel.isActive && channel.releaseArtifactId
  ).length;
  const emptyCount = channels.filter(
    (channel) => channel.isActive && !channel.releaseArtifactId
  ).length;
  const pausedCount = channels.filter((channel) => !channel.isActive).length;

  const visibleChannels = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return channels.filter((channel) => {
      const status = !channel.isActive
        ? 'paused'
        : channel.releaseArtifactId
          ? 'serving'
          : 'empty';
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (!normalizedQuery) return true;
      const version = channel.releaseAppVersionId
        ? releaseAppVersionById.get(channel.releaseAppVersionId)
        : undefined;
      const app = channel.releaseAppId
        ? releaseAppById.get(channel.releaseAppId)
        : version
          ? releaseAppById.get(version.releaseAppId)
          : undefined;
      const artifact = channel.releaseArtifactId
        ? artifactById.get(channel.releaseArtifactId)
        : undefined;
      return [
        app?.name,
        app?.platform,
        version?.version,
        channel.channel,
        channel.environment,
        artifact?.version,
      ].some((value) => value?.toLowerCase().includes(normalizedQuery));
    });
  }, [
    artifactById,
    channels,
    query,
    releaseAppById,
    releaseAppVersionById,
    statusFilter,
  ]);

  function handleEditChannel(channel: ReleaseChannelRow) {
    openDialog({
      kind: 'channel',
      channelId: channel.releaseChannelId,
      organizationId: channel.organizationId,
      projectId: channel.projectId || activeProject?.id || '',
      tenantEnvironmentId:
        channel.tenantEnvironmentId || activeEnvironment?.tenantEnvironmentId || '',
      environment: channel.environment,
      lookupEnvironment: channel.environment,
      channel: channel.channel,
      lookupChannel: channel.channel,
      bundleCdnHostOverride: channel.bundleCdnHostOverride || '',
      isActive: channel.isActive,
    });
  }

  return (
    <SectionCard
      label="Current delivery"
      title="App versions running now"
      description={
        channels.length > 0
          ? `${channels.length} targets · ${servingCount} serving · ${emptyCount} awaiting a bundle · ${pausedCount} paused`
          : 'Targets are generated when you add an app version.'
      }
      action={
        channels.length > 0 ? (
          <div className="bundleToolbar">
            <label className="bundleSearch">
              <Search size={14} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search app, version, environment, or bundle"
                aria-label="Search release targets"
              />
            </label>
            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(
                  event.target.value as 'all' | 'serving' | 'empty' | 'paused'
                )
              }
              aria-label="Filter release targets by status"
            >
              <option value="all">All statuses</option>
              <option value="serving">Serving</option>
              <option value="empty">Awaiting bundle</option>
              <option value="paused">Paused</option>
            </select>
          </div>
        ) : undefined
      }
    >
      {channels.length === 0 ? (
        <EmptyState
          icon={<GitBranch size={20} />}
          title="No release targets"
          description="Add an app version before choosing which bundle it should serve."
        />
      ) : visibleChannels.length === 0 ? (
        <EmptyState
          compact
          icon={<Search size={18} />}
          title="No matching app versions"
          description="Try another app, version, environment, bundle, or status."
          action={
            <button
              type="button"
              className="secondaryButton"
              onClick={() => {
                setQuery('');
                setStatusFilter('all');
              }}
            >
              Clear filters
            </button>
          }
        />
      ) : (
        <div className="releaseTableWrap" role="region" aria-label="Live release targets table" tabIndex={0}>
          <table className="releaseTable releaseTableTargets">
            <thead>
              <tr>
                <th>App</th>
                <th>Store version</th>
                <th>Environment</th>
                <th>Running bundle</th>
                <th>Status</th>
                <th>Last changed</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {visibleChannels.map((channel) => {
                const version = channel.releaseAppVersionId
                  ? releaseAppVersionById.get(channel.releaseAppVersionId)
                  : undefined;
                const app = channel.releaseAppId
                  ? releaseAppById.get(channel.releaseAppId)
                  : version
                    ? releaseAppById.get(version.releaseAppId)
                    : undefined;
                const artifact = channel.releaseArtifactId
                  ? artifactById.get(channel.releaseArtifactId)
                  : undefined;
                const changedAt =
                  latestPromotionByLane.get(
                    `${channel.environment}/${channel.channel}`
                  )?.created || channel.updated;

                return (
                  <tr key={channel.releaseChannelId}>
                    <td>
                      <div className="releaseIdentity">
                        <strong>{app?.name || 'App not recorded'}</strong>
                        <span>{app?.platform?.toUpperCase() || 'Legacy target'}</span>
                      </div>
                    </td>
                    <td>
                      <div className="releaseIdentity">
                        <strong>{version?.version || 'Not recorded'}</strong>
                        <span>{app?.platform?.toUpperCase() || 'Platform unavailable'} target</span>
                      </div>
                    </td>
                    <td>
                      <span className={`envPill env-${channel.environment}`}>
                        {channel.environment}
                      </span>
                    </td>
                    <td>
                      <div className="releaseIdentity">
                        <strong>
                          {artifact
                            ? formatArtifactSummary(artifact)
                            : 'No bundle deployed'}
                        </strong>
                        {artifact ? (
                          <span
                            className="releaseCodeLine"
                            title={getArtifactTechnicalId(artifact)}
                          >
                            <code>{getArtifactTechnicalId(artifact)}</code>
                            <CopyButton
                              value={getArtifactTechnicalId(artifact)}
                              label="Copy exact build ID"
                            />
                          </span>
                        ) : (
                          <span>This target is not serving an update</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <StatusChip
                        tone={
                          !channel.isActive
                            ? 'neutral'
                            : channel.releaseArtifactId
                              ? 'success'
                              : 'warn'
                        }
                      >
                        {!channel.isActive
                          ? 'Paused'
                          : channel.releaseArtifactId
                            ? 'Serving'
                            : 'Awaiting bundle'}
                      </StatusChip>
                    </td>
                    <td>{formatRelativeDate(changedAt)}</td>
                    <td className="releaseTableAction">
                      <button
                        type="button"
                        className="secondaryButton"
                        onClick={() => handleEditChannel(channel)}
                      >
                        Settings
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

export { ChannelsPage };
